import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import Checkbox from '@/common/components/Checkbox';
import GoBackButton from '@/common/components/GoBackButton';
import TextArea from '@/common/components/TextArea';
import TextField from '@/common/components/TextField';
import { DateInput } from '@/common/components/DateInput/DateInput';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { FileUploader } from '@/common/components/FileUploader';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { ErrorMessage } from '@/common/components/ErrorMessage';
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { useCreateTmaisBanner } from '@/features/tmaisBanners/hooks/useCreateTmaisBanner';
import {
  bannerFileConstraints,
  createTmaisBannerSchema,
  CreateTmaisBannerValidationSchema,
} from '@/features/tmaisBanners/schemas/create.schema';
import { FormTransformers } from '@/common/utils/formTransformers';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useGoBack } from '@/common/hooks/useGoBack';
import { ChevronsRight, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const CreateTmaisBanner = () => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const goBack = useGoBack();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();
  const hasCreatePermission = !isUserPermissionsPending ? hasUserPermission('Admin::Tmais::Banners::Create') : true;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    setError: setFormError,
    formState: { errors },
  } = useForm<CreateTmaisBannerValidationSchema>({
    resolver: zodResolver(createTmaisBannerSchema),
    defaultValues: {
      add_expiration: false,
    },
  });

  const watchAddExpiration = watch('add_expiration');

  useEffect(() => {
    if (!watchAddExpiration) {
      setValue('expires_at_date', undefined);
      setValue('expires_at_time', undefined);
      clearErrors(['expires_at_date', 'expires_at_time']);
    }
  }, [watchAddExpiration, setValue, clearErrors]);

  const { mutate: createBanner, isPending: isCreating } = useCreateTmaisBanner({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tmaisBanners"] });
      goBack({ fallback: '/tmais-banners' });
    },
    onError: FormTransformers.errorsTransformer<CreateTmaisBannerValidationSchema>({
      setError: setFormError,
      setMessages,
    }),
  });

  const onSubmit: SubmitHandler<CreateTmaisBannerValidationSchema> = (values) => {
    createBanner(values as any);
  };

  return (
    <ErrorMessageWrapper statusCode={hasCreatePermission ? 200 : 403}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/tmais-banners" />
            <p className="text-white-400 font-normal">Anúncios e banners</p>
            <ChevronsRight className="text-white-400" />
            <h2>Novo banner</h2>
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline-error"
              onClick={() => goBack({ fallback: '/tmais-banners' })}
              disabled={isCreating}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={isCreating}
              disabled={isCreating}
            >
              Cadastrar banner
            </Button>
          </div>
        </div>

        <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
          <p className="text-xl font-bold">Detalhes do Banner</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateInput
              fieldName="active_at_date"
              control={control as any}
              label="Data de envio"
              markAsRequired
              errors={errors}
            />
            <TextField
              label="Hora para envio"
              type="time"
              markAsRequired
              errorMessage={errors.active_at_time?.message}
              disabled={isCreating}
              LeftIcon={<Clock className="w-5 h-5 text-white-400" />}
              {...register('active_at_time')}
            />
          </div>

          <TextField
            label="Redirecionar clique para"
            markAsRequired
            errorMessage={errors.mobile_redirect_to?.message}
            disabled={isCreating}
            {...register('mobile_redirect_to')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch mb-2">
            <Controller
              control={control}
              name="file"
              render={({ field }) => {
                const previewUrl = field.value instanceof File ? URL.createObjectURL(field.value) : null;

                return (
                  <div className="flex flex-col w-full h-full min-h-[220px]">
                    {previewUrl && (
                      <div className="relative mb-2 w-full h-36 bg-neutral-op-8 rounded-md overflow-hidden shrink-0">
                        <img src={previewUrl} alt="Preview selecionado" className="absolute inset-0 w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" />
                      </div>
                    )}

                    <div className="flex-1 w-full h-full bg-neutral-op-8 border border-transparent rounded-md transition-colors
                      [&>div]:w-full [&>div]:h-full [&>div]:border-none [&>div]:!bg-transparent [&>div]:m-0 [&>div]:p-0
                      [&_label]:w-full [&_label]:h-full [&_label]:flex [&_label]:flex-col [&_label]:justify-center [&_label]:items-center [&_label]:border-none [&_label]:!bg-transparent [&_label]:m-0
                    ">
                      <FileUploader
                        title={field.value?.name ? `Selecionado: ${field.value.name}` : `Anexe uma imagem ou GIF em 1920x1080 até ${bannerFileConstraints.maxSizeInMb}MB`}
                        accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
                        handleChange={(file) => {
                          field.onChange(file);
                          clearErrors('file');
                        }}
                        fileName={field.value?.name}
                        maxFileSizeInMb={bannerFileConstraints.maxSizeInMb}
                        disabled={isCreating}
                        setError={(message) => {
                          if (message) setFormError('file', { message });
                          else clearErrors('file');
                        }}
                      />
                    </div>
                    {errors.file?.message && (
                      <div className="mt-2">
                        <ErrorMessage name="file" message={errors.file?.message} />
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <div className="flex flex-col gap-4 h-full">
              <TextField
                label="Nome do banner"
                markAsRequired
                errorMessage={errors.title?.message}
                disabled={isCreating}
                {...register('title')}
              />

              <TextArea
                label="Descrição interna"
                markAsRequired
                errorMessage={errors.description?.message}
                disabled={isCreating}
                className="flex-1 h-full resize-none"
                rows={5}
                {...register('description')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-2">
            <Checkbox
              label="Adicionar data de expiração"
              className="text-white-800"
              disabled={isCreating}
              {...register('add_expiration')}
            />

            {watchAddExpiration && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <DateInput
                  fieldName="expires_at_date"
                  control={control as any}
                  label="Data expiração envio"
                  markAsRequired
                  errors={errors}
                />
                <TextField
                  label="Hora para expiração"
                  type="time"
                  markAsRequired
                  errorMessage={errors.expires_at_time?.message}
                  disabled={isCreating}
                  LeftIcon={<Clock className="w-5 h-5 text-white-400" />}
                  {...register('expires_at_time')}
                />
              </div>
            )}
          </div>
        </Box>

      {messages && <ModalMessageSimple messages={messages as string[]} setMessages={setMessages as any} isError />}
      </form>
    </ErrorMessageWrapper>
  );
};

export default CreateTmaisBanner;
